/**
 * Aspect colouring — "colour by aspect" visualization mode.
 *
 * Pick an aspect key (e.g. `security.zone`); every distinct string value
 * under that key gets a stable colour (hashed from the value, so the same
 * value is always the same colour across reloads and across diagrams).
 * Module widgets are then tinted by their value's colour, and a legend
 * maps value → colour.
 *
 * Only string-valued labels (aspects) participate — bare values are
 * module references, not classification axes. Pure + deterministic.
 */

import type { Surface, Interface, Module } from "@archlang/scene/arch-model";
// Canonical aspect-value colour lives in viewer-core so the board overlay and
// the process system cylinders seed from ONE hash (no drift). Imported for the
// helpers below AND re-exported for the board call sites that import it here.
import { aspectColorFor } from "@archlang/viewer-core";
export { aspectColorFor };

/** A serialized label value is the raw core `Value` object. */
function stringValue(value: unknown): string | undefined {
  if (value && typeof value === "object" && (value as { kind?: string }).kind === "string") {
    return (value as { text: string }).text;
  }
  return undefined;
}

/** The string value a module carries for `key`, if any. */
export function aspectValueOf(module: Module, key: string): string | undefined {
  for (const l of module.aspects) {
    if (l.path.join(".") === key) return stringValue(l.value);
  }
  return undefined;
}

function walk(modules: readonly Module[], visit: (m: Module) => void): void {
  for (const m of modules) {
    visit(m);
    walk(m.children, visit);
  }
}

/** Distinct labels.keys that have at least one string value, sorted. */
export function aspectKeysOf(modules: readonly Module[]): string[] {
  const keys = new Set<string>();
  walk(modules, (m) => {
    for (const l of m.aspects) {
      if (stringValue(l.value) !== undefined) keys.add(l.path.join("."));
    }
  });
  return [...keys].sort();
}

export interface LegendEntry {
  readonly value: string;
  readonly color: string;
}

/**
 * Board ids (`moduleKey` = id ?? qualified name) of every module whose
 * value for `key` equals `value`. Used to highlight an aspect's members
 * from the legend.
 */
export function aspectMemberIds(
  modules: readonly Module[],
  key: string,
  value: string
): string[] {
  const ids: string[] = [];
  const visit = (mods: readonly Module[], parent: string): void => {
    for (const m of mods) {
      const q = parent ? `${parent}.${m.name}` : m.name;
      if (aspectValueOf(m, key) === value) ids.push(m.id ?? q);
      visit(m.children, q);
    }
  };
  visit(modules, "");
  return ids;
}

/**
 * Qualified interface paths (`Module.Surface?.Interface`, matching an
 * edge's `toInterfacePath.join(".")`) of every interface that *itself*
 * carries `key === value`. Lets the legend highlight the specific edges
 * targeting those interfaces, not a member module's every connection.
 */
export function aspectMemberInterfaces(
  modules: readonly Module[],
  key: string,
  value: string
): string[] {
  const paths: string[] = [];
  const labelMatch = (labels: readonly { path: readonly string[]; value: unknown }[]): boolean =>
    labels.some((l) => l.path.join(".") === key && stringValue(l.value) === value);

  const visitInterfaces = (ifaces: readonly Interface[], parent: string): void => {
    for (const i of ifaces) {
      if (labelMatch(i.aspects)) paths.push(`${parent}.${i.name}`);
    }
  };
  const visitSurfaces = (surfaces: readonly Surface[], parent: string): void => {
    for (const f of surfaces) {
      const fq = `${parent}.${f.name}`;
      visitInterfaces(f.interfaces, fq);
      visitSurfaces(f.surfaces, fq);
    }
  };
  const visit = (mods: readonly Module[], parent: string): void => {
    for (const m of mods) {
      const q = parent ? `${parent}.${m.name}` : m.name;
      visitInterfaces(m.interfaces, q);
      visitSurfaces(m.surfaces, q);
      visit(m.children, q);
    }
  };
  visit(modules, "");
  return paths;
}

/** Distinct values for `key` with their colours, sorted by value. */
export function aspectLegend(modules: readonly Module[], key: string): LegendEntry[] {
  const values = new Set<string>();
  walk(modules, (m) => {
    const v = aspectValueOf(m, key);
    if (v !== undefined) values.add(v);
  });
  return [...values].sort().map((value) => ({ value, color: aspectColorFor(value) }));
}
